import { useEffect, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import { socketAuth } from '@/config/socketAuth'
import { BrainCircuit } from 'lucide-react'
import { velosterHealthService, VelosterHealthStatus } from '../services/velosterHealthService'
import { useAuth } from '../contexts/AuthContext'
import { API_CONFIG } from '../config/api'
import { whenVisible } from '@/utils/whenVisible'

const POLL_INTERVAL = 5 * 60 * 1000 // 5 min

type Tone = 'red' | 'amber' | 'neutral'

const TONE_CLASSES: Record<Tone, {
  badge: string
  dot: string
  popover: string
  iconBox: string
  icon: string
  title: string
  body: string
}> = {
  red: {
    badge: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    dot: 'bg-red-500',
    popover: 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-800',
    iconBox: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-900 dark:text-red-300',
    body: 'text-red-800 dark:text-red-300',
  },
  amber: {
    badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
    popover: 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-800',
    iconBox: 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-800',
    icon: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-900 dark:text-amber-300',
    body: 'text-amber-800 dark:text-amber-300',
  },
  neutral: {
    badge: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200',
    dot: 'bg-green-500',
    popover: 'bg-surface border-zinc-200 dark:border-zinc-700',
    iconBox: 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700',
    icon: 'text-zinc-600 dark:text-zinc-400',
    title: 'text-zinc-900 dark:text-zinc-200',
    body: 'text-zinc-700 dark:text-zinc-200',
  },
}

const VELOSTER_DOT: Record<VelosterHealthStatus['level'], string> = {
  operational: 'bg-green-500 dark:bg-green-400',
  degraded: 'bg-amber-500 dark:bg-amber-400',
  outage: 'bg-red-500 dark:bg-red-400',
  scheduled_off: 'bg-zinc-500 dark:bg-zinc-500',
  unknown: 'bg-zinc-400 dark:bg-zinc-600',
}

const VELOSTER_LABEL: Record<VelosterHealthStatus['level'], string> = {
  operational: 'Operacional',
  degraded: 'Instável',
  outage: 'Indisponível',
  scheduled_off: 'Desligado (programado)',
  unknown: 'Desconhecido',
}

const TITULO: Record<VelosterHealthStatus['level'], string> = {
  operational: 'Leitura por IA operacional.',
  degraded: 'Instabilidade no servidor de IA.',
  outage: 'Servidor de IA fora do ar.',
  scheduled_off: 'Servidor de IA desligado (programado).',
  unknown: 'Sem informação do servidor de IA.',
}

const CORPO: Record<VelosterHealthStatus['level'], string> = {
  operational: 'Nenhuma interrupção detectada no momento.',
  degraded:
    'A leitura de documentos e a Comparação de Docs podem estar mais lentas ou com falhas. Documentos já enviados serão processados assim que o serviço normalizar.',
  outage:
    'A leitura de documentos e a Comparação de Docs estão indisponíveis. Documentos já enviados serão processados assim que o serviço voltar.',
  scheduled_off:
    'A leitura automática está pausada por desligamento programado. Documentos enviados agora entram na fila e são processados quando o servidor voltar.',
  unknown: 'Não foi possível consultar o servidor de inferência agora.',
}

/**
 * Saúde da leitura por IA. Desde o desligamento do Gemini (17/09/2026) a
 * inferência é só do servidor próprio, então o badge vem inteiro de
 * `GET /veloster/health` — não há mais consulta a status de nuvem.
 */
export default function AiStatusBanner() {
  const { hasCap } = useAuth()
  const isDev = hasCap('can_use_dev_tools')

  const [veloster, setVeloster] = useState<VelosterHealthStatus | null>(null)
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        const data = await velosterHealthService.getStatus()
        if (!cancelled) setVeloster(data)
      } catch {
        // silently ignore
      }
    }

    check()
    const id = setInterval(whenVisible(check), POLL_INTERVAL)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  // Telemetria detalhada (GPUs, filas) — dev-only, em tempo real pela mesma
  // sala/gateway do AI Status Dashboard, sem loop de polling adicional.
  useEffect(() => {
    if (!isDev) return
    let cancelled = false

    const socket = io(API_CONFIG.BASE_URL, {
      withCredentials: true,
      auth: socketAuth,
      // Em produção, alguns proxies bloqueiam upgrade websocket.
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    })
    socket.emit('join_ai_status', {})

    const onVelosterUpdate = (payload: {
      level: VelosterHealthStatus['level']
      gpus: VelosterHealthStatus['gpus']
      counts: Record<string, number>
      failureRate: number
      queue?: VelosterHealthStatus['queue']
      gpu1Mode?: string | null
      queueDurable?: boolean | null
      updated_at: string
    }) => {
      if (cancelled) return
      setVeloster({
        level: payload.level,
        gpus: payload.gpus,
        counts: payload.counts,
        failureRate: payload.failureRate,
        queue: payload.queue,
        gpu1Mode: payload.gpu1Mode,
        queueDurable: payload.queueDurable,
        checkedAt: payload.updated_at,
      })
    }
    socket.on('veloster_health_updated', onVelosterUpdate)

    return () => {
      cancelled = true
      socket.off('veloster_health_updated', onVelosterUpdate)
      socket.disconnect()
    }
  }, [isDev])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const level = veloster?.level ?? null
  // `unknown` = falha ao consultar, não incidente: não alarma quem só usa o
  // sistema (antes, o "desconhecido" do status da nuvem virava badge amarelo).
  const isProblem = level === 'degraded' || level === 'outage'
  const isOutage = level === 'outage'

  // Não-dev: só aparece se houver problema. Dev/admin: badge sempre visível,
  // pra permitir inspecionar as GPUs.
  if (!isDev && !isProblem) return null
  if (isDev && !veloster) return null // aguarda 1ª checagem antes de renderizar

  const tone: Tone = isOutage ? 'red' : isProblem ? 'amber' : 'neutral'
  const classes = TONE_CLASSES[tone]

  const nivel = level ?? 'unknown'
  const label = VELOSTER_LABEL[nivel]

  return (
    <div
      ref={wrapperRef}
      // Wrapper só agrupa o hover; não é interativo em si — o botão dentro
      // dele já cobre clique/teclado (mesmo padrão de Tooltip.tsx).
      role="presentation"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className="relative flex items-center gap-1.5 text-xs border-r border-border-subtle pr-4 mr-4"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={`flex items-center gap-1.5 px-2 py-1 rounded-[3px] transition-opacity hover:opacity-80 ${classes.badge}`}
      >
        <BrainCircuit className="w-4 h-4" />
        <span className="font-semibold">IA</span>
        <span className={`w-2 h-2 rounded-full inline-block ${classes.dot}`} />
        <span className="text-xs font-medium">{label}</span>
      </button>

      {open && (
        <div
          role="dialog"
          className={`absolute top-full right-0 mt-2 rounded-[5px] border shadow-lg p-4 z-50 ${isDev ? 'w-96' : 'w-80'} ${classes.popover}`}
        >
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-8 h-8 rounded-[5px] border flex items-center justify-center ${classes.iconBox}`}>
              <BrainCircuit className={`w-4 h-4 ${classes.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Leitura por IA
              </p>
              <p className={`text-sm font-bold leading-snug ${classes.title}`}>
                {TITULO[nivel]}
              </p>
              <p className={`text-sm leading-snug mt-1 ${classes.body}`}>
                {CORPO[nivel]}
              </p>
            </div>
          </div>

          {isDev && (
            <div className="mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-700">
              <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
                Veloster (local)
              </p>
              {veloster ? (
                <>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className={`w-2 h-2 rounded-full inline-block ${VELOSTER_DOT[veloster.level]}`} />
                    <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                      {VELOSTER_LABEL[veloster.level]}
                    </span>
                    {veloster.failureRate > 0 && (
                      <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                        · falhas {Math.round(veloster.failureRate * 100)}%
                      </span>
                    )}
                  </div>
                  {(veloster.queue ||
                    (veloster.gpu1Mode && veloster.gpu1Mode !== 'classificador-35b') ||
                    veloster.queueDurable === false) && (
                    <div className="mb-2 space-y-0.5 text-[11px] text-zinc-600 dark:text-zinc-300">
                      {veloster.queue &&
                        Object.entries(veloster.queue).map(([model, q]) => (
                          <div key={model}>
                            Fila {model}: {q.depth} {q.depth === 1 ? 'job' : 'jobs'}
                            {q.depth > 0 && ` · ~${Math.max(1, Math.round(q.backlog_s / 60))} min`}
                          </div>
                        ))}
                      {veloster.gpu1Mode && veloster.gpu1Mode !== 'classificador-35b' && (
                        <div className="text-amber-700 dark:text-amber-300">
                          gpu1 em swap ({veloster.gpu1Mode}) — classificação aguardando na fila
                        </div>
                      )}
                      {veloster.queueDurable === false && (
                        <div className="text-red-700 dark:text-red-300">
                          Fila do gateway sem durabilidade (Postgres fora do ar)
                        </div>
                      )}
                    </div>
                  )}
                  {veloster.gpus.length > 0 ? (
                    <ul className="space-y-1.5">
                      {veloster.gpus.map((gpu) => (
                        <li
                          key={gpu.id}
                          className="rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-2 py-1.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                              <span
                                className={`w-1.5 h-1.5 rounded-full inline-block ${
                                  gpu.ready ? 'bg-green-500' : 'bg-red-500'
                                }`}
                              />
                              GPU {gpu.id}
                            </span>
                            <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                              {gpu.ready ? 'pronta' : 'indisponível'}
                            </span>
                          </div>
                          <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-zinc-600 dark:text-zinc-400">
                            <span>{gpu.temp_c != null ? `${gpu.temp_c}°C` : '—'}</span>
                            <span>{gpu.util_pct != null ? `${gpu.util_pct}%` : '—'}</span>
                            <span>
                              {gpu.mem_used_mb != null && gpu.mem_total_mb != null
                                ? `${gpu.mem_used_mb}/${gpu.mem_total_mb} MB`
                                : '—'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">Nenhuma GPU reportada.</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Sem dados de telemetria.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
